<script setup lang="ts">

import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { computed, ref } from 'vue'
import { T } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThePremium from '@/components/ThePremium.vue'
import type { MysteryBoxItem } from '@/types/mystery-box'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import { formatUnshortenedNumber } from '@/helpers/utils'
import DefaultBOGOBadge from '@/components/periodic/default-BOGO/DefaultBOGOBadge.vue'
import { useShop } from '@/composables/game/shop'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
  boxItem: MysteryBoxItem
  ecommerceItem: EcommerceItem
}>()

const emit = defineEmits<{
  (e: 'onPurchase'): void
}>()

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
  canHaveSale,
} = useEcommerceItemDetailsPurchase(props.ecommerceItem)
const { isDefaultBOGO, isBOGO } = useShop()

const isLoading = ref(false)

const itemMultiplier = computed(() => {
  if (props.ecommerceItem.id.endsWith('X1')) return 1
  if (props.ecommerceItem.id.endsWith('X3')) return 3
  if (props.ecommerceItem.id.endsWith('X5')) return 5
  return 1
})

const hasBOGO = computed(() => canHaveSale.value && isBOGO.value)
const hasDefaultBOGO = computed(() => hasBOGO.value && isDefaultBOGO.value)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      setTimeout(() => {
        emit('onPurchase')
        isLoading.value = false
      }, priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? 350 : 1)
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

</script>

<template>
  <StarsBalancePill class="absolute top-[50px] left-0" />
  <div class="relative">
    <img
      class="size-[160px] object-cover"
      :src="boxItem.image"
    >
    <DefaultBOGOBadge
      v-if="hasDefaultBOGO"
      class="absolute left-4 top-4 w-[55px] h-auto z-[3]"
    />
  </div>
  <div class="mt-6 text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
    {{ boxItem.name }}<span>{{ " " }}</span><span class="lowercase">x{{
      itemMultiplier * (hasBOGO ? 2 : 1)
    }} = ${{ starPrice.usdAmount }}</span>
  </div>
  <div class="mt-12 w-full flex flex-col gap-y-3">
    <BaseButton
      class="w-full"
      :loading="isBuyingWithStars"
      @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
    >
      <span
        v-if="!isBuyingWithStars"
        class="flex items-center gap-x-2"
      >
        <T
          key-name="starter_pack.buy_with"
          default-value="Buy with"
        />
        <ThePremium :with-tooltip="false" />
        <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
      </span>
      <T
        v-else
        key-name="lingo.giveaway.transaction.loading_cta"
        default-value="Processing..."
      />
    </BaseButton>

    <template v-if="showTonPurchase">
      <div class="relative w-full">
        <div class="absolute right-12 -top-1 rotate-[20deg]">
          <TonDiscountBadge label="-10%" />
        </div>
        <BaseButton
          style-type="tertiary"
          :loading="isBuyingWithTon"
          class="w-full"
          @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
        >
          <span
            v-if="!isBuyingWithTon"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <img
              src="@/assets/images/ton-symbol.png"
              class="size-5 min-w-[20px] object-cover"
            >
            <span>{{ tonPrice?.amount }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>
      </div>

      <div class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium">
        <T
          key-name="transaction.process_description"
          default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
        />
      </div>
    </template>
  </div>
</template>

<style scoped>

</style>